// src/pages/agents/AgentWidgetsTab.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, // TableCell & TableRow for TableHead
  Button, IconButton, CircularProgress, Tooltip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItemButton, ListItemText, Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'; // Icons specific to this page's top actions
import { useOutletContext, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import InfoBox from '../../components/common/InfoBox';
import { widgetService } from '../../api/widgetService';
import EmbedCodeModal from '../../components/widgets/EmbedCodeModal';
import WidgetVersionListItem from '../../components/widgets/WidgetVersionListItem'; // IMPORT THE NEW COMPONENT

const AgentWidgetsTab = () => {
  const { agent } = useOutletContext();
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  const [embedModalProps, setEmbedModalProps] = useState({
    open: false, versionId: null, templateId: null, templateName: null,
  });

  const [selectTemplateModalOpen, setSelectTemplateModalOpen] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateInModal, setSelectedTemplateInModal] = useState(null);

  const fetchAgentVersions = useCallback(async () => {
    if (!agent?.id) { // Guard against missing agent ID
      setError("Agent ID not available to fetch widgets.");
      setLoading(false);
      setVersions([]);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await widgetService.getVersionsByAgent(agent.id);
    if (res.isError) {
      setError(res.message || 'Failed to load widget versions for this agent.');
      setVersions([]);
    } else {
      setVersions(Array.isArray(res) ? res.sort((a,b) => new Date(b.updated_at) - new Date(a.updated_at)) : []);
    }
    setLoading(false);
  }, [agent?.id]); // Depend on agent.id

  useEffect(() => {
    fetchAgentVersions();
  }, [fetchAgentVersions]);

  const handleActivate = async (versionIdToActivate) => {
    setActivatingId(versionIdToActivate);
    const res = await widgetService.activateVersion(versionIdToActivate);
    if (!res.isError) {
      toast.success(res.message || 'Version activated successfully.');
      fetchAgentVersions();
    } else {
      toast.error(res.message || 'Failed to activate version.');
    }
    setActivatingId(null);
  };

  const handleDeleteVersion = async (versionId) => {
    const versionToDelete = versions.find(v => v.id === versionId);
    if (!window.confirm(`Are you sure you want to delete version "${versionToDelete?.version_name || versionToDelete?.name || 'this version'}"? This action cannot be undone.`)) return;
    
    setLoading(true);
    const res = await widgetService.deleteVersion(versionId);
    setLoading(false);

    if (!res.isError) {
      toast.success('Widget version deleted successfully.');
      setVersions(vs => vs.filter(v => v.id !== versionId));
    } else {
      toast.error(res.message || 'Failed to delete version.');
    }
  };

  const handleOpenEmbedModal = (versionObject) => {
    setEmbedModalProps({
      open: true,
      versionId: versionObject.id,
      templateId: versionObject.template?.id,
      templateName: versionObject.template?.name,
    });
  };

  const handleCloseEmbedModal = () => {
    setEmbedModalProps({ open: false, versionId: null, templateId: null, templateName: null });
  };

  const handleOpenSelectTemplateModal = async () => {
    setSelectTemplateModalOpen(true);
    setSelectedTemplateInModal(null);
    setLoadingTemplates(true);
    const res = await widgetService.listTemplates(); 
    if (res.isError) {
      toast.error(res.message || "Failed to load widget templates.");
      setAllTemplates([]);
    } else {
      setAllTemplates(Array.isArray(res) ? res : []);
    }
    setLoadingTemplates(false);
  };

  const handleCloseSelectTemplateModal = () => {
    setSelectTemplateModalOpen(false);
    setSelectedTemplateInModal(null);
  };

  const handleTemplateSelectedInModal = (template) => {
    setSelectedTemplateInModal(template);
  };

  const handleProceedToCreateVersion = () => {
    if (!selectedTemplateInModal) {
      toast.warn("Please select a template.");
      return;
    }
    navigate(`/widgets/${selectedTemplateInModal.id}/versions/new`, {
      state: { prefillAgentId: agent.id, fromAgentContext: true } 
    });
    handleCloseSelectTemplateModal();
  };

  const handleNavigateToCreateTemplate = () => {
    navigate('/widgets/new', {
      state: { prefillAgentIdForVersion: agent.id, fromAgentContext: true }
    });
    handleCloseSelectTemplateModal();
  };

  if (loading && versions.length === 0) return <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>;
  
  if (error && versions.length === 0) return <InfoBox type="error" title="Error Loading Widget Versions">{error}</InfoBox>;

  return (
    <Box>
      <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems="center" mb={2} spacing={1}>
        <Typography variant="h6">Widget Versions Linked to: {agent?.name || 'Agent'}</Typography>
        <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh list">
                <span>
                <IconButton onClick={fetchAgentVersions} disabled={loading || !agent?.id}>
                    {loading && versions.length > 0 ? <CircularProgress size={24} /> : <RefreshIcon />}
                </IconButton>
                </span>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenSelectTemplateModal} disabled={loading || !agent?.id}>
            Create & Link New Version
            </Button>
        </Stack>
      </Stack>
      
      {error && versions.length > 0 && ( 
        <Alert severity="warning" sx={{mb:2}}>{error}</Alert>
      )}

      {versions.length === 0 && !loading && (
        <InfoBox type="info" title="No Widget Versions Linked">
          This agent is not yet linked to any widget versions. Click "Create & Link New Version" to associate one.
        </InfoBox>
      )}

      {versions.length > 0 && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 700 }} aria-label="agent widget versions table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', pl:2, width: '35%' }}>Version Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Template</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', pr:2, width: '20%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map(v => (
                <WidgetVersionListItem
                  key={v.id}
                  version={v}
                  onActivate={handleActivate}
                  onDelete={handleDeleteVersion}
                  onOpenEmbedModal={handleOpenEmbedModal}
                  isActivating={activatingId === v.id}
                  isLoadingActions={loading && versions.length > 0}
                  showTemplateName={true}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <EmbedCodeModal
        open={embedModalProps.open}
        onClose={handleCloseEmbedModal}
        versionId={embedModalProps.versionId}
        templateId={embedModalProps.templateId}
        templateName={embedModalProps.templateName}
      />

      <Dialog open={selectTemplateModalOpen} onClose={handleCloseSelectTemplateModal} fullWidth maxWidth="sm">
        <DialogTitle>Select Widget Template</DialogTitle>
        <DialogContent>
          {loadingTemplates ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}><CircularProgress /></Box>
          ) : allTemplates.length === 0 ? (
            <Alert severity="info" sx={{mt: 1}}>
              No widget templates found. You must create a template before creating a version.
            </Alert>
          ) : (
            <List component="nav" sx={{ maxHeight: 300, overflow: 'auto', mt:1, border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1}}>
              {allTemplates.map((template) => (
                <ListItemButton
                  key={template.id}
                  selected={selectedTemplateInModal?.id === template.id}
                  onClick={() => handleTemplateSelectedInModal(template)}
                  divider={true}
                >
                  <ListItemText 
                    primary={template.name} 
                    secondary={template.description || `ID: ...${template.id.slice(-6)}`} 
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{justifyContent: 'space-between', px: {xs:2, sm:3}, pb: {xs:2, sm:2.5} }}>
            <Button 
                onClick={handleNavigateToCreateTemplate}
                startIcon={<LaunchIcon />}
                variant="text" 
            >
                Create New Template
            </Button>
            <Box>
                <Button onClick={handleCloseSelectTemplateModal}>Cancel</Button>
                <Button
                    onClick={handleProceedToCreateVersion}
                    variant="contained"
                    disabled={!selectedTemplateInModal || loadingTemplates || loading}
                    sx={{ml:1}}
                >
                    Next: Create Version
                </Button>
            </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentWidgetsTab;